/**
 * Subscribers — reader newsletter lists per masthead (email M3, 2026-10-08).
 *
 * Accountable mode: every figure says where it comes from. Source is each
 * masthead's Mailjet Subscribers list, mirrored into assayer_email_subscribers
 * by the publishing-service reconcile every ~6 h. The page shows list health,
 * sign-ups over time and sign-up attribution (page, form, referring site,
 * utm), on the LATEST sign-up or the FIRST touch.
 *
 * Addresses are masked by default; "Reveal addresses" fetches them in full
 * and the backend logs who revealed which list.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowLeft, Eye, EyeOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { TableScroll } from '../components/ui/table-scroll.js';
import {
  getSubscriberSummary,
  listSubscribers,
  type SignupBasis,
  type SubscriberSummary,
  type SubscriberDimension,
  type SubscriberListSummary,
} from '../lib/api/notifications.js';
import { SITE_LABEL } from '../lib/analyticsShape.js';

const PERIODS: Array<{ days: number; label: string }> = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 0, label: 'All time' },
];

const DIMENSIONS: Array<{ key: SubscriberDimension; label: string; hint: string }> = [
  { key: 'page', label: 'Page', hint: 'The page the form was on (an article, a company hub, /subscribe/).' },
  { key: 'ctx', label: 'Form', hint: 'footer = the form on every page; page = the subscribe page or a hub form.' },
  { key: 'ref', label: 'Referring site', hint: 'Where the visit started: the referring domain, direct, or internal.' },
  { key: 'utm_source', label: 'utm_source', hint: 'Campaign tag on the link the visitor followed.' },
  { key: 'utm_campaign', label: 'utm_campaign', hint: 'Campaign tag on the link the visitor followed.' },
];

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', {
    timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function mastheadLabel(name: string | null): string {
  return (name && SITE_LABEL[name]) || name || 'Unknown masthead';
}

export default function SubscribersPage() {
  const [days, setDays] = useState(30);
  const [basis, setBasis] = useState<SignupBasis>('latest');
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['subscriber-summary', days, basis],
    queryFn: () => getSubscriberSummary(days, basis),
  });
  const run = data?.last_reconcile;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-grey-500 mb-1">
            <Link to="/notifications/queue" className="inline-flex items-center gap-1 hover:text-grey-700">
              <ArrowLeft className="h-3.5 w-3.5" />
              Notifications
            </Link>
          </div>
          <h1 className="text-2xl font-semibold text-grey-900">
            Subscribers
            <Link to="/notifications/digest" className="ml-3 align-middle text-sm font-normal text-primary-700 hover:underline">Weekly digest →</Link>
          </h1>
          <p className="text-sm text-grey-600 mt-1 max-w-3xl">
            Readers on each masthead&apos;s newsletter list: how many there are, when they signed up, and where
            from. Source: the Mailjet Subscribers lists, mirrored every ~6 hours. Sign-up details are recorded
            since 6 Oct 2026; earlier subscribers show as <em>(legacy)</em>.
          </p>
          <p className="text-xs text-grey-500 mt-2">
            {run ? (
              <>Last sync {fmtDateTime(run.finished_at)}: {run.lists_ok} lists synced
                {run.lists_failed > 0 && <span className="text-danger-600">, {run.lists_failed} failed</span>}
                {run.error && <span className="text-danger-600"> ({run.error})</span>}.</>
            ) : 'No sync run recorded yet.'}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-grey-300 rounded hover:bg-grey-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="inline-flex rounded border border-grey-300 overflow-hidden" role="group" aria-label="Period">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              aria-pressed={days === p.days}
              className={`px-3 py-1.5 text-sm ${days === p.days ? 'bg-primary-600 text-white' : 'bg-surface text-grey-700 hover:bg-grey-50'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded border border-grey-300 overflow-hidden" role="group" aria-label="Attribution basis">
          {(['latest', 'first'] as const).map((b) => (
            <button
              key={b}
              onClick={() => setBasis(b)}
              aria-pressed={basis === b}
              className={`px-3 py-1.5 text-sm ${basis === b ? 'bg-primary-600 text-white' : 'bg-surface text-grey-700 hover:bg-grey-50'}`}
              title={b === 'latest' ? 'Where they most recently signed up' : 'Where they first signed up to this masthead'}
            >
              {b === 'latest' ? 'Latest sign-up' : 'First touch'}
            </button>
          ))}
        </div>
      </div>

      {data && <NetworkHeadline data={data} />}

      {isLoading && <p className="text-sm text-grey-500">Loading…</p>}
      {isError && (
        <p className="text-sm text-danger-600 inline-flex items-center gap-1">
          <AlertTriangle className="h-4 w-4" /> {(error as Error)?.message ?? 'Failed to load'}
        </p>
      )}
      {data && data.lists.length === 0 && (
        <p className="text-sm text-grey-500">No subscriber lists mirrored yet. The first sync runs within ~6 hours of deploy.</p>
      )}

      <div className={`flex flex-col gap-6 ${isFetching ? 'opacity-60 transition-opacity' : 'transition-opacity'}`}>
        {data?.lists.map((l) => (
          <MastheadCard key={l.list_id} list={l} days={days} basis={basis} />
        ))}
      </div>
    </div>
  );
}

function NetworkHeadline({ data }: { data: SubscriberSummary }) {
  const domains = data.network.internal_domains.map((d) => `@${d}`).join(', ');
  return (
    <section className="mb-6 bg-surface rounded-xl border border-grey-200 p-5">
      <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-wide text-grey-500">Subscribers, Assayer Network</div>
          <div className="text-4xl font-semibold text-grey-900">{data.network.unique_active.toLocaleString()}</div>
          <div className="text-xs text-grey-500 mt-0.5">unique readers, active</div>
        </div>
        <ul className="flex flex-wrap gap-x-6 gap-y-2">
          {data.lists.map((l) => (
            <li key={l.list_id}>
              <div className="text-xs text-grey-500">{mastheadLabel(l.target_name)}</div>
              <div className="text-xl font-semibold text-grey-900">{l.active.toLocaleString()}</div>
            </li>
          ))}
        </ul>
      </div>
      <p className="text-xs text-grey-500 mt-3">
        A reader on two lists counts once in the network total and once in each masthead.
        {data.network.internal > 0 && (
          <> {data.network.internal} internal or test address{data.network.internal === 1 ? '' : 'es'} ({domains}) excluded from every figure.</>
        )}
      </p>
    </section>
  );
}

function Tile({ label, value, hint, tone }: { label: string; value: number; hint: string; tone?: 'warn' }) {
  return (
    <div className="rounded-lg border border-grey-200 bg-surface px-3 py-2" title={hint}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-grey-500">{label}</div>
      <div className={`text-xl font-semibold ${tone === 'warn' && value > 0 ? 'text-warning-700' : 'text-grey-900'}`}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function MastheadCard({ list, days, basis }: { list: SubscriberListSummary; days: number; basis: SignupBasis }) {
  const [showList, setShowList] = useState(false);
  const periodLabel = PERIODS.find((p) => p.days === days)?.label.toLowerCase() ?? `${days} days`;
  return (
    <section className="bg-surface rounded-xl border border-grey-200 p-4 flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-grey-900">{mastheadLabel(list.target_name)}</h2>
          <p className="text-xs text-grey-500">
            Mailjet list {list.list_id} · mirrored {fmtDateTime(list.last_synced_at)}
          </p>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Tile label="Active" value={list.active} hint="On the list and not unsubscribed" />
        <Tile label={`New (${periodLabel})`} value={list.new_signups} hint="Sign-ups in the period, by Mailjet sign-up date" />
        <Tile label="Unsubscribed" value={list.unsubscribed} hint="Still on the list but unsubscribed" />
        <Tile label="Bounced / excluded" value={list.excluded} hint="Mailjet excludes them from campaigns (hard bounce or spam complaint)" tone="warn" />
        <Tile label="Left the list" value={list.removed} hint="No longer on the Mailjet list" />
        <Tile label="Unknown context" value={list.unknown_context} hint="Signed up in the period but the form sent no page or source (an old open tab or a direct submission)" tone="warn" />
      </div>
      {list.internal > 0 && (
        <p className="text-xs text-grey-400 -mt-2">
          Plus {list.internal} internal/test address{list.internal === 1 ? '' : 'es'}, not counted.
        </p>
      )}

      <div>
        <h3 className="text-sm font-semibold text-grey-900">Sign-ups per day</h3>
        <p className="text-xs text-grey-500 mb-1">Sydney dates, by Mailjet sign-up date.</p>
        {list.series.length === 0 ? (
          <div className="h-[140px] flex items-center justify-center text-sm text-grey-400">No sign-ups in this period</div>
        ) : (
          <div className="h-[140px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={list.series} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid stroke="var(--viz-grid)" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip />
                <Bar dataKey="count" name="Sign-ups" fill="var(--viz-series-1)" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-grey-900">
          Where they signed up ({basis === 'latest' ? 'latest sign-up' : 'first touch'}, {periodLabel})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
          {DIMENSIONS.map((d) => (
            <div key={d.key} className="rounded-lg border border-grey-100">
              <div className="px-3 py-1.5 border-b border-grey-100 text-xs font-semibold text-grey-700" title={d.hint}>{d.label}</div>
              <table className="w-full text-xs">
                <tbody>
                  {(list.breakdowns[d.key] ?? []).length === 0 && (
                    <tr><td className="px-3 py-1.5 text-grey-400">—</td></tr>
                  )}
                  {(list.breakdowns[d.key] ?? []).map((r) => (
                    <tr key={r.value} className="border-t border-grey-50">
                      <td className="px-3 py-1 text-grey-700 break-all">{r.value}</td>
                      <td className="px-3 py-1 text-right text-grey-900 font-medium">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>

      <div>
        <button
          onClick={() => setShowList((s) => !s)}
          className="text-sm text-primary-700 hover:underline"
        >
          {showList ? 'Hide subscribers' : 'Show subscribers'}
        </button>
        {showList && <SubscriberTable listId={list.list_id} />}
      </div>
    </section>
  );
}

function SubscriberTable({ listId }: { listId: number }) {
  const [reveal, setReveal] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['subscribers', listId, reveal],
    queryFn: () => listSubscribers(listId, reveal),
  });
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-grey-500">
          {reveal ? 'Full addresses shown. This view is logged.' : 'Addresses are masked.'}
        </p>
        <button
          onClick={() => setReveal((r) => !r)}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-grey-300 rounded hover:bg-grey-50"
        >
          {reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {reveal ? 'Mask addresses' : 'Reveal addresses'}
        </button>
      </div>
      {isLoading ? (
        <p className="text-sm text-grey-500">Loading…</p>
      ) : (
        <TableScroll>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-grey-500">
                <th className="px-2 py-1 font-medium">Email</th>
                <th className="px-2 py-1 font-medium">Signed up</th>
                <th className="px-2 py-1 font-medium">Status</th>
                <th className="px-2 py-1 font-medium">Latest page</th>
                <th className="px-2 py-1 font-medium">Referring site</th>
                <th className="px-2 py-1 font-medium">utm</th>
                <th className="px-2 py-1 font-medium">First page</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((r) => (
                <tr
                  key={`${r.email}-${r.subscribed_at}`}
                  className={`border-t border-grey-100 ${r.is_internal ? 'text-grey-400' : ''}`}
                  title={r.is_internal ? 'Internal/test address, not counted' : undefined}
                >
                  <td className="px-2 py-1 font-mono">{r.email}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{fmtDateTime(r.subscribed_at)}</td>
                  <td className="px-2 py-1">
                    {r.is_internal ? 'internal' : r.removed_at ? 'left list' : r.is_unsubscribed ? 'unsubscribed' : r.is_excluded_from_campaigns ? 'excluded' : 'active'}
                  </td>
                  <td className="px-2 py-1 break-all">{r.latest_is_legacy ? '(legacy)' : r.latest_page ?? '(unknown)'}</td>
                  <td className="px-2 py-1">{r.latest_ref ?? '—'}</td>
                  <td className="px-2 py-1">
                    {[r.latest_utm_source, r.latest_utm_medium, r.latest_utm_campaign].filter(Boolean).join(' / ') || '—'}
                  </td>
                  <td className="px-2 py-1 break-all">{r.first_page ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </div>
  );
}
